import { createRef, Dispatch, RefObject, SetStateAction, useEffect, useMemo, useRef, useState } from "react";
import { GridCard } from "../../GridCard/GridCard";
import styles from "./Columns.module.scss";
import { Typography } from "@/frontend/shared/ui/Typography/Typography";
import { DragDropContext, Droppable, Draggable, DropResult, DragStart } from "@hello-pangea/dnd";
import { TPortfolioCompany, TStageResponse, TStagesResponse } from "../../../const/const";
import { updateStageForCompany } from "../../../model/services/updateStageForCompany";
import { DropshadowMenu } from "@/frontend/shared/ui/DropshadowMenu";
import { renameStage } from "../../../model/services/renameStage";
import { DeleteCompanyModal } from "@/frontend/features/Company/ui/DeleteCompany/DeleteCompanyModal/DeleteCompanyModal";
import { MoveModal } from "@/frontend/features/Company/ui/MoveCompany/MoveModal/MoveModal";
import { buildDropshadowOptions } from "../../../lib/buildDropshadowOptions";
import { getRoutePortfolioCompanyProfile } from "@/frontend/shared/const/router";
import { EmptyPageContent } from "@/frontend/components/EmptyPageContent/EmptyPageContent";
import InfiniteScroll from "react-infinite-scroll-component";
import { useHistoricalNavigate } from "@/frontend/shared/lib/useHistoricalNavigate/useHistoricalNavigate";
import { AddCompaniesFixedModal } from "../../AddCompaniesFixedModal/AddCompaniesFixedModal";
import { TCompanyInfo, TPipeline } from "../../../model/types/types";
import { AddTagsCardModal } from "@/frontend/components/AddTagsCardModal/ui/AddTagsCardModal";
import { blockedStages, EContent } from "@/frontend/components/DashboardHeader/types/types";
import { Tag } from "@/frontend/shared/ui/Tag";
import { DotsThreeVerticalIcon } from "@/frontend/shared/assets/icons";
import { snackbarConstructor } from "@/frontend/shared/ui/CustomNotifications/ui/Snackbar/snackbarConstructor";
import { reportErrorToSentry } from "@/frontend/shared/lib/reportErrorToSentry/reportErrorToSentry";
import { RenameStageModal } from "@/frontend/components/RenameStageModal/RenameStageModal";
import { deleteStage } from "../../../model/services/deleteStage";
import { ConfirmationModal } from "@/frontend/shared/ui/ConfirmationModal/ConfirmationModal";
import { archiveCompany } from "../../../model/services/archiveCompany";
import clsx from "clsx";
import { useScreenWidth } from "@/frontend/shared/lib/hooks/useScreenWidth";
import { autoScrollerOptions } from "../const/const";
import { AxiosError } from "axios";
import { EDataStatus } from "@/frontend/features/Company/model/types/types";
import { stageOptions } from "@/frontend/components/AddCompaniesPage/ui/CompanySection/const/const";
import { AddTagsModalV2 } from "@/frontend/components/AddTagsModalV2/ui/AddTagsModalV2";
import { patchPortfolioBulkAssignTags } from "@/frontend/features/Company/model/patchPortfolioBulkAssignTags";
import { removeTagFromPortfolioCompany } from "@/frontend/features/Company/model/removeTagFromPortfolioCompany";
import { ELOCAL_STORAGE_KEY } from "@/frontend/shared/const/const";
type TGridCardsProps = {
  data: TStagesResponse;
  triggerRefetch: () => void;
  // setFilters: (value: TFilterDataProps) => void;
  applyFilter: () => void;
  refetchStageData: () => void;
  setCurrentPageForStage: ({ stageId, page }: { stageId: number; page: number }) => void;
  currentPages: { stageId: number; page: number }[];
  setColumnCompaniesData: Dispatch<SetStateAction<TStagesResponse>>;
  portfolioCompaniesBulkData: TCompanyInfo[];
  headersData: any;
  refetchHeaders: () => void;
  filteredProcessingCompanies: number[];
  fileUploadProgresses: Record<string, number>;
  triggerRefetchBulkData: () => void;
  setFileUploadProgresses: Dispatch<SetStateAction<Record<string, number>>>;
  setBulkDataInfo: Dispatch<SetStateAction<TCompanyInfo[]>>;
  setDashboardContent: Dispatch<SetStateAction<EContent>>;
  numFilters: number;
  refetchPipelines: () => void;
};

type ScrollRefs = {
  [key: number]: RefObject<HTMLDivElement>;
};

export function Columns({
  data,
  triggerRefetch,
  setCurrentPageForStage,
  currentPages,
  setColumnCompaniesData,
  portfolioCompaniesBulkData,
  headersData,
  refetchHeaders,
  filteredProcessingCompanies,
  fileUploadProgresses,
  setFileUploadProgresses,
  triggerRefetchBulkData,
  setBulkDataInfo,
  setDashboardContent,
  numFilters,
  refetchPipelines
}: TGridCardsProps) {
  const [openedDropdown, setOpenedDropdown] = useState(-1);
  const [selectedOption, setSelectedOption] = useState<string>("");
  const [shownModal, setShownModal] = useState<{ type: string; id: number | null }>({ type: "", id: null });
  const [cardTags, setCardTags] = useState<{ name: string; id: number }[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { navigate } = useHistoricalNavigate();
  const scrollRefs = useRef<ScrollRefs>({});

  const columns = data.stages;

  // get headerOptions: {stageId, stageName}[]
  const headerOptions = data?.stages?.map((stage) => {
    return { value: String(stage.id), label: stage.name };
  });

  // send stage update to backend + refetch
  async function updateStage({
    stageId,
    movedItemId,
    existingItem
  }: {
    stageId: number;
    movedItemId: number;
    existingItem: TPortfolioCompany | null;
  }) {
    await updateStageForCompany(movedItemId, stageId, existingItem?.id).catch((error) => {
      console.error("Error:", error);
    });

    refetchHeaders();
  }

  const columnsWrapperRef = useRef<HTMLDivElement>(null);

  const isMobile = useScreenWidth() <= 768;

  function onDragStart(result: DragStart) {
    if (!columnsWrapperRef.current) {
      return;
    }
    if (isMobile) {
      columnsWrapperRef.current.style.scrollSnapType = "none";
    }
  }

  // update stage for the company on frontend and backend
  async function onDragEnd(result: DropResult) {
    if (!columnsWrapperRef.current) {
      return;
    }
    if (isMobile) {
      columnsWrapperRef.current.style.scrollSnapType = "x mandatory";
    }
    const { source, destination } = result;

    if (!destination || (destination.droppableId === source.droppableId && destination.index === source.index)) {
      return;
    }

    setColumnCompaniesData((prevData) => {
      const newColumns = prevData?.stages?.map((stage) => ({
        ...stage,
        portfolioCompanies: stage?.portfolioCompanies?.filter(
          (company) => company.parsingStatus !== EDataStatus.processing && company.parsingStatus !== EDataStatus.parsing
        )
      }));

      const startColIndex = newColumns.findIndex((col) => col.id.toString() === source.droppableId);
      const finishColIndex = newColumns.findIndex((col) => col.id.toString() === destination.droppableId);

      const movedItem = newColumns[startColIndex].portfolioCompanies[source.index];
      newColumns[startColIndex].portfolioCompanies = newColumns[startColIndex].portfolioCompanies.filter(
        (_, index) => index !== source.index
      );

      const newFinishCompanies = [
        ...newColumns[finishColIndex].portfolioCompanies.slice(0, destination.index),
        movedItem,
        ...newColumns[finishColIndex].portfolioCompanies.slice(destination.index)
      ];

      newColumns[finishColIndex].portfolioCompanies = newFinishCompanies;

      const existingItem = destination.index === 0 ? null : newFinishCompanies[destination.index - 1];

      updateStage({
        stageId: newColumns[finishColIndex].id,
        movedItemId: movedItem.id,
        existingItem
      }).catch(console.error);

      // checkAndLoadMoreData(newColumns[startColIndex]);
      checkAndLoadMoreData(newColumns[finishColIndex]);

      return { stages: newColumns };
    });

    refetchHeaders();
  }

  // if we have more than 10 companies loaded go to the next page
  function checkAndLoadMoreData(column: TStageResponse) {
    const currentPage = currentPages.find((page) => page.stageId === column.id)?.page || 0;
    if (
      column?.portfolioCompanies?.filter((item) => !filteredProcessingCompanies?.includes(item.id)).length >
      15 * currentPage
    ) {
      const nextPage = currentPage + 1;

      setCurrentPageForStage({
        stageId: column.id,
        page: nextPage
      });
    }
  }

  async function handleStageRename(columnId: number, name: string) {
    setIsLoading(true);
    await renameStage(columnId, name)
      .then(() => {
        triggerRefetch();
        setShownModal({ type: "", id: null });
        snackbarConstructor("Stage was renamed successfully", "normal");
      })
      .catch((error) => {
        console.error("Error:", error);
        snackbarConstructor("Something went wrong", "error");
      })
      .finally(() => setIsLoading(false));
  }

  async function handleArchive(id: number) {
    setIsLoading(true);
    await archiveCompany([id])
      .then(() => {
        triggerRefetch();
        setShownModal({ type: "", id: null });
        snackbarConstructor("Company was archived successfully", "normal");
      })
      .catch((error) => {
        console.error("Error:", error);
        snackbarConstructor("Something went wrong", "error");
      })
      .finally(() => setIsLoading(false));
  }

  // delete stage function
  async function handleStageDelete(columnId: number) {
    setIsLoading(true);
    await deleteStage(columnId)
      .then(() => {
        triggerRefetch();
        setShownModal({ type: "", id: null });
        snackbarConstructor("Stage was deleted successfully", "normal");
      })
      .catch((error) => {
        console.error("Error:", error);
        const errorMessage = error?.response?.data?.data?.message || "Something went wrong";
        snackbarConstructor(errorMessage, "error");
      })
      .finally(() => setIsLoading(false));
  }

  function getStageIdByName(name: string) {
    return data.stages.find((stage) => Boolean(stage.name === name))?.id;
  }

  // on click open rename modal
  function onClickRename(column: TStageResponse) {
    setOpenedDropdown(-1);
    setShownModal({ type: "stageRename", id: column.id });
  }

  // on click open delete modal
  function onClickDelete(column: TStageResponse) {
    setOpenedDropdown(-1);
    setShownModal({ type: "stageDelete", id: column.id });
  }

  // do we have any companies or no
  const isPortfolioCompanies = columns.filter((col) => col.portfolioCompanies.length > 0).length > 0;

  // get / create scrollref
  function getScrollRef(idx: number) {
    if (!scrollRefs.current[idx]) {
      scrollRefs.current[idx] = createRef<HTMLDivElement>();
    }
    return scrollRefs.current[idx];
  }

  // refetch when card is deleted
  function refetchOnDelete() {
    refetchHeaders();
    triggerRefetch();
    refetchPipelines();
  }

  function memoizeGridCards(
    column: TStageResponse,
    navigate: (route: string) => void,
    setShownModal: Dispatch<SetStateAction<{ type: string; id: number | null }>>,
    triggerRefetch: () => void,
    setCardTags: Dispatch<SetStateAction<{ name: string; id: number }[]>>,
    snapshot: any,
    index: number,
    provided: any,
    totalCompaniesForCurrentColumn: number
  ) {
    const containerId = `cardsContainer-${index}`;
    const scrollRef = getScrollRef(index);

    if (column.portfolioCompanies.length === 0) {
      return <div className={styles.emptyContainer} id={containerId} />;
    }

    return (
      <div id={containerId} className={styles.cardsContainer} ref={scrollRef}>
        <InfiniteScroll
          dataLength={column.portfolioCompanies.length}
          next={() => {
            const currentPage = currentPages.find((page) => page.stageId === column.id)?.page || 0;
            const nextPage = currentPage + 1;
            setCurrentPageForStage({
              stageId: column.id,
              page: nextPage
            });
          }}
          hasMore={totalCompaniesForCurrentColumn > column.portfolioCompanies.length}
          loader={<Typography variant="body-2">Loading...</Typography>}
          scrollableTarget={containerId}
          style={{ overflow: "visible" }}
        >
          {column?.portfolioCompanies
            ?.filter((company) => !filteredProcessingCompanies?.includes(company.id))
            ?.map((card, cardIndex) => (
              <Draggable
                key={`${column.id}-${card.id}-${card.createdAt}`}
                draggableId={`${column.id}-${card.id}-${card.parsingStatus}`}
                index={cardIndex}
                isDragDisabled={!!snapshot.draggingFromThisWith}
              >
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    onClick={() => navigate(getRoutePortfolioCompanyProfile(card.id))}
                    // onDoubleClick={() => setShownModal({ type: "delete", id: card.id })}
                  >
                    <GridCard
                      card={card}
                      index={cardIndex}
                      setShownModal={setShownModal}
                      triggerRefetch={triggerRefetch}
                      id={card.id}
                      addCardTags={setCardTags}
                      scrollContainerRef={scrollRef}
                    />
                  </div>
                )}
              </Draggable>
            ))}
        </InfiniteScroll>
        {provided.placeholder}
      </div>
    );
  }

  const columnNames = useMemo(() => columns.map((column) => column.name), [columns]);

  const addTagsFn = async (id: number, tags: string[]) => {
    await patchPortfolioBulkAssignTags([id], tags);
  };

  const removeTagsFn = async (id: number, tagIds: number[]) => {
    await removeTagFromPortfolioCompany({ tagIds, companyId: id });
  };

  const lsPipeline = localStorage.getItem(ELOCAL_STORAGE_KEY.PIPELINE);
  const initialPipeline = lsPipeline ? (JSON.parse(lsPipeline) as TPipeline) : undefined;

  return (
    <DragDropContext onDragEnd={onDragEnd} onDragStart={onDragStart} autoScrollerOptions={autoScrollerOptions}>
      <div className={styles.superWrapper}>
        <div className={styles.wrapper} id={"columnsWrapperId"} ref={columnsWrapperRef}>
          {columns?.map((column, index) => {
            const totalCompaniesForCurrentColumn =
              headersData?.stages?.find((stage: TStageResponse) => stage.id === column.id)?.totalPortfolioCompanies ||
              0;

            return (
              <Droppable key={`${column.id}-${index}`} droppableId={column.id.toString()}>
                {(provided, snapshot) => (
                  <div className={styles.container}>
                    <div className={styles.columnPlaceholder} ref={provided.innerRef} {...provided.droppableProps} />
                    <DropshadowMenu
                      isOpen={index === openedDropdown}
                      options={buildDropshadowOptions({
                        onClickRename: () => onClickRename(column),
                        onClickDelete: () => onClickDelete(column),
                        styles,
                        hasItems: column.portfolioCompanies.length > 0
                      })}
                      onClose={() => setOpenedDropdown(-1)}
                      top="10px"
                      right="10px"
                      width="320px"
                      dividerIndexRate={1}
                    />
                    <div className={clsx(styles.headerContainer, totalCompaniesForCurrentColumn === 0 && styles.empty)}>
                      <div className={styles.headerSubcontainer}>
                        <div className={styles.headerTextContainer}>
                          <div>
                            <Typography variant="label-3" color="greyscale900" id="pipelinePage-columnName">
                              {column.name}
                            </Typography>
                          </div>
                          <Tag size="small" className={styles.Tag} id="pipelinePage-columnTotal">
                            {totalCompaniesForCurrentColumn}
                          </Tag>
                        </div>
                        <div className={styles.headerIconsContainer}>
                          {!blockedStages.includes(column.name) && (
                            <DotsThreeVerticalIcon
                              onClick={() => setOpenedDropdown(index)}
                              id="pipelinePage-columnContextMenuButton"
                            />
                          )}
                        </div>
                      </div>
                    </div>
                    {memoizeGridCards(
                      column,
                      navigate,
                      setShownModal,
                      triggerRefetch,
                      setCardTags,
                      snapshot,
                      index,
                      provided,
                      totalCompaniesForCurrentColumn
                    )}

                    <RenameStageModal
                      isLoading={isLoading}
                      initialValue={column.name}
                      isOpen={shownModal.id == column.id && shownModal.type == "stageRename"}
                      onClose={() => setShownModal({ type: "", id: null })}
                      renameStage={(name: string) => handleStageRename(column.id, name)}
                      columns={columnNames}
                    />
                    {/* for stage delete */}
                    <ConfirmationModal
                      isOpen={shownModal.id == column.id && shownModal.type == "stageDelete"}
                      onClose={() => setShownModal({ type: "", id: null })}
                      isDisabled={isLoading}
                      onConfirm={() => handleStageDelete(column.id)}
                      title="Delete Stage?"
                      confirmButtonText="Delete"
                      content={`Are you sure you want to delete stage "${column.name}"?`}
                      dangerConfirm
                    />
                  </div>
                )}
              </Droppable>
            );
          })}
          <AddCompaniesFixedModal
            portfolioCompaniesBulkData={portfolioCompaniesBulkData}
            fileUploadProgresses={fileUploadProgresses}
          />
        </div>
      </div>

      {/* Empty page */}
      {!isPortfolioCompanies &&
        (numFilters > 0 ? (
          <div className={styles.noCompaniesFound}>
            <Typography variant="label-4" color="greyscale600">
              No matching companies found
            </Typography>
          </div>
        ) : (
          <div className={styles.emptyPageContainer}>
            <EmptyPageContent
              triggerRefetch={triggerRefetchBulkData}
              triggerRefetchAll={triggerRefetch}
              setBulkDataInfo={setBulkDataInfo}
              setFileUploadProgresses={setFileUploadProgresses}
              setDashboardContent={setDashboardContent}
              stageOptions={headerOptions}
            />
          </div>
        ))}

      {/* Modals */}
      <DeleteCompanyModal
        isOpen={shownModal.type === "delete" && shownModal.id !== null}
        triggerRefetch={refetchOnDelete}
        id={shownModal.id || -1}
        onClose={() => setShownModal({ type: "", id: null })}
      />
      <MoveModal
        isOpen={shownModal.type === "move" && shownModal.id !== null}
        onClose={() => setShownModal({ type: "", id: -1 })}
        id={shownModal.id ?? 0}
        triggerRefetch={triggerRefetch}
        initialPipeline={initialPipeline?.name}
      />
      <AddTagsModalV2
        isOpen={shownModal.type === "addTags" && shownModal.id !== null}
        id={shownModal.id !== null ? shownModal.id : 0}
        onClose={() => {
          setShownModal({ type: "", id: null });
          setCardTags([]);
        }}
        triggerRefetch={triggerRefetch}
        currentTags={cardTags.map((tag) => tag.name)}
        addTagsFn={addTagsFn}
        removeTagsFn={removeTagsFn}
      />
      <ConfirmationModal
        isOpen={!!shownModal.id && shownModal.type == "archive"}
        onClose={() => setShownModal({ type: "", id: null })}
        isDisabled={isLoading}
        onConfirm={() => handleArchive(shownModal.id || 0)}
        title="Move company to archive?"
        confirmButtonText="Archive"
        content="Once archived, the company will be removed from the pipeline but can be restored at any time if needed. No data will be deleted, but the company will no longer appear in the pipeline view."
      />
    </DragDropContext>
  );
}
